import React from 'react';
import { Container, Typography, Box, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const FAQ: React.FC = () => (
  <Container maxWidth="md">
    <Box sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>Frequently Asked Questions</Typography>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>What is TransferHub?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            TransferHub helps professionals discover and connect with others looking to transfer to similar locations or roles. You can create requests, find matches, and message potential matches directly.
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>How do I contact a TransferHub match?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            Open the Matches page under TransferHub, then click "Contact Match". A message box will open so you can start chatting immediately.
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Can I search transfer requests by location or profession?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            Yes. Use the Search page under TransferHub to filter by profession and location. Searches are case-insensitive.
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>How do I register as a service provider?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            Click on "Provider Register" in the navigation or footer, fill out the form, and submit your details. Your profile will be reviewed and approved by an admin before it appears online.
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>How do I book a service?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            Use the search bar or browse categories to find a provider, then click "Book" and fill out the booking form.
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>How can I contact support?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            Use the "Contact Us" page to send a message to our support team. We'll get back to you as soon as possible.
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>How are providers approved?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            All provider registrations are reviewed by an admin before being listed to ensure quality and trust.
          </Typography>
        </AccordionDetails>
      </Accordion>
      
      {/* TransferHub Questions */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>What is TransferHub and how does it work?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            TransferHub is a specialized platform within NeighborhoodPulse that helps professionals discover transfer opportunities, connect with potential matches, and facilitate meaningful career transitions. You can create profiles, post job opportunities, make transfer requests, and connect with other professionals looking for similar opportunities.
          </Typography>
        </AccordionDetails>
      </Accordion>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>How do I create a TransferHub profile?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            Navigate to TransferHub in the main menu, then click on "My Profile" to create or edit your professional profile. Include your skills, experience, preferred locations, and career aspirations to help others find you.
          </Typography>
        </AccordionDetails>
      </Accordion>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Can I post job opportunities on TransferHub?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            Yes! Go to the "Jobs" section in TransferHub to post job opportunities or browse available positions. This is perfect for employers looking for specific skills or professionals seeking new challenges.
          </Typography>
        </AccordionDetails>
      </Accordion>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>How does the matching system work in TransferHub?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            Our intelligent matching algorithm connects professionals based on complementary skills, location preferences, and career goals. The system analyzes your profile and transfer requests to find professionals who are looking for exactly what you have to offer.
          </Typography>
        </AccordionDetails>
      </Accordion>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Is my TransferHub profile information private?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            TransferHub includes robust privacy controls. You can control who can see your profile and contact you. All communications are encrypted and secure, ensuring professional confidentiality during career transitions.
          </Typography>
        </AccordionDetails>
      </Accordion>
      
      {/* Additional General Questions */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>How do I leave a review for a service provider?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            After booking a service, you can leave a review by going to the "Reviews" section or visiting the provider's profile page. Your honest feedback helps other community members make informed decisions.
          </Typography>
        </AccordionDetails>
      </Accordion>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Can I update my service provider information?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            Yes, service providers can update their information through their provider dashboard. Changes are reviewed by admin before going live to maintain quality standards.
          </Typography>
        </AccordionDetails>
      </Accordion>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>What should I do if I have a problem with a service provider?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            If you experience issues with a service provider, please contact our support team through the "Contact Us" page. We take all complaints seriously and will work to resolve any problems promptly.
          </Typography>
        </AccordionDetails>
      </Accordion>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>How do I get notifications about new announcements or opportunities?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            When you're logged in, you'll see notifications in the top navigation bar. You can also check the "Announcements" section regularly for community updates and important information.
          </Typography>
        </AccordionDetails>
      </Accordion>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Is NeighborhoodPulse free to use?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            Yes, NeighborhoodPulse is free for residents to browse services, read reviews, and connect with local providers. Service providers may have different registration requirements, but the platform itself is free to use.
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Box>
  </Container>
);

export default FAQ; 